import { useLanguage } from "@/context/LanguageContext";
import { useId } from "react";
import { Link } from "react-router-dom";
import { QuoteButton } from "@/components/shared";
import { SITE, TRAVELLER_SEATS, VEHICLE_PREFERENCES } from "@/config/site";
import { TARIFF_POLICY, formatINR, getVehicleTariff } from "@/config/tariffs";

const VEHICLE_GROUPS = [
  {
    id: "sedan",
    title: "Sedan cars",
    options: VEHICLE_PREFERENCES.Sedan.filter((model) => model !== "No preference"),
  },
  {
    id: "suv-mpv",
    title: "SUVs and MPVs",
    options: VEHICLE_PREFERENCES["SUV / MPV"].filter((model) => model !== "No preference"),
  },
  {
    id: "tempo-traveller",
    title: "Tempo Traveller seating options",
    options: TRAVELLER_SEATS.map((seats) => `Tempo Traveller ${seats} seater`),
  },
];

// localGuidance is optional location-specific copy. Compact changes the layout,
// while keeping every model and seating option visible in the rendered HTML.
export const VehicleOptions = ({
  city = SITE.baseCity,
  localGuidance = "",
  title,
  compact = false,
  headingLevel = 2,
  showQuote = true,
  className = "",
}) => {
  const { t, tParts, locale, cityName: localizeCity, localizePath } = useLanguage();
  const headingId = useId();
  const cityName = typeof city === "string" && city.trim() ? city.trim() : SITE.baseCity;
  const displayCityName = localizeCity(cityName);
  const guidanceText = t(localGuidance);
  const hasLocalizedGuidance = locale !== "hi" || guidanceText !== localGuidance || /[\u0900-\u097f]/.test(guidanceText);
  const Heading = headingLevel === 3 ? "h3" : "h2";
  const GroupHeading = headingLevel === 3 ? "h4" : "h3";
  const linkClassName = "font-semibold text-amber-700 underline underline-offset-4 hover:text-amber-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2";

  return (
    <section aria-labelledby={headingId} className={className} data-testid="vehicle-options">
      <Heading id={headingId} className="text-2xl font-extrabold text-slate-900">
        {title ? t(title) : t("Car hire & Tempo Traveller rental in {city}", { city: displayCityName })}
      </Heading>
      <p className="mt-3 text-base leading-relaxed text-slate-600">
        {t("For taxi services and cab booking in {city}, ask about car rental or Tempo Traveller hire with a driver. Share your pickup, destination and travel dates when you enquire.", { city: displayCityName })}
      </p>
      {localGuidance && hasLocalizedGuidance && (
        <p className="mt-3 text-sm leading-relaxed text-slate-600" data-testid="vehicle-options-local-guidance">
          {guidanceText}
        </p>
      )}

      <div className={`mt-6 grid gap-4 ${compact ? "" : "lg:grid-cols-3"}`}>
        {VEHICLE_GROUPS.map((group) => (
          <div key={group.id} className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid={`vehicle-options-${group.id}`}>
            <GroupHeading className="text-lg font-bold text-slate-900">{t(group.title)}</GroupHeading>
            <ul className={`mt-4 text-sm text-slate-700 ${compact ? "grid gap-2 sm:grid-cols-2" : "space-y-2"}`}>
              {group.options.map((option) => {
                const tariff = getVehicleTariff(option);
                const seating = option.match(/^Tempo Traveller (\d+) seater$/);
                const optionLabel = seating ? t("Tempo Traveller {seats} seater", { seats: seating[1] }) : t(option);
                return (
                  <li key={option} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
                    <span>
                      {optionLabel}
                      <span className="block font-semibold text-slate-900" data-testid={`vehicle-options-rate-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {tariff ? t("{amount} / KM outstation", { amount: formatINR(tariff.outstationRatePerKm) }) : t("Quote on request · confirm passenger capacity")}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900" data-testid="vehicle-options-availability">
        <p>{t("Outstation rates include fuel and a driver, with a minimum of {outstationMinKmPerDay} KM per day. Running is calculated garage-to-garage. {gstPercent}% GST, toll, parking, border charges, applicable night charges and driver meals are extra.", { outstationMinKmPerDay: TARIFF_POLICY.outstationMinKmPerDay, gstPercent: TARIFF_POLICY.gstPercent })}</p>
        <p className="mt-2">{t("Models and seating options are subject to availability for your route and date. Share passengers and luggage; the team confirms the selected vehicle and complete fare before booking.")}</p>
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm leading-relaxed text-slate-600">
          {tParts("See our {fleetRatesLink} or explore {tempoOptionsLink}.", {
            fleetRatesLink: <Link key="fleet" to={localizePath("/fleet/")} className={linkClassName} data-testid="vehicle-options-fleet-link">{t("full fleet rates and local hire charges")}</Link>,
            tempoOptionsLink: <Link key="tempo" to={localizePath("/tempo-traveller/")} className={linkClassName} data-testid="vehicle-options-tempo-link">{t("Tempo Traveller hire and rental options")}</Link>,
          })}
        </p>
        {showQuote && (
          <QuoteButton prefill={{ pickup_city: cityName }} testId="vehicle-options-quote-button">
            {t("Request a vehicle quote")}
          </QuoteButton>
        )}
      </div>
    </section>
  );
};

export default VehicleOptions;
